import styled from "styled-components";
import { theme } from "../../constants/theme";

export const AddressNotFound = styled.div`
  color: ${theme.color.error};
  margin-top: 0.65rem;
  font-size: 0.9rem;
`;

export const SmallSpinner = styled.div`
  height: 17px;
  width: 17px;
`;

export const CountyNote = styled.small`
  display: block;
  margin-top: 1rem;
  color: ${theme.color.textMuted};
  line-height: 1.55;

  b {
    color: ${theme.color.slate};
    font-weight: ${theme.weight.semibold};
  }

  a {
    color: ${theme.color.teal};
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;

    &:hover {
      color: ${theme.color.salmonDeep};
    }
  }
`;

// Styled wrapper around the LookupAddress InputGroup. Gives the address
// field a clear bordered pill identity so it reads as an input, not
// floating text. Focus-within lifts the pill with a salmon ring.
export const AddressPillWrap = styled.div`
  width: 100%;
  margin: 0 auto;

  .form-group-no-border {
    background: ${theme.color.surface};
    border: 1.5px solid ${theme.color.border};
    border-radius: ${theme.radius.pill};
    box-shadow: ${theme.shadow.sm};
    padding: 0.35rem 0.75rem 0.35rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition:
      border-color ${theme.motion.base},
      box-shadow ${theme.motion.base},
      transform ${theme.motion.base};
  }

  .form-group-no-border:hover {
    border-color: ${theme.color.borderStrong};
  }

  .form-group-no-border:focus-within {
    border-color: ${theme.color.salmon};
    box-shadow: ${theme.shadow.sm}, ${theme.shadow.ring};
    transform: translateY(-1px);
  }

  .input-group-prepend {
    margin: 0;
  }

  /* Doubled "&&" increases specificity so this rule wins
     over the FormCard's .input-group-text rule that sets a
     tan background (intended for the email + property-detail
     inputs, not for this address pill).  Without this fix,
     the icon sits inside a sunk-tone rectangle that reads
     as a stray decorative shape. */
  && .input-group-text {
    background: transparent !important;
    border: none !important;
    padding: 0 0.25rem 0 0 !important;
    color: ${theme.color.slate};
    font-size: 1.05rem;
    border-radius: 0 !important;
  }

  .react-autosuggest__container {
    flex: 1;
    width: 100%;
    position: relative;
  }

  .react-autosuggest__input {
    width: 100%;
    border: none !important;
    background: transparent !important;
    padding: 0.65rem 0.5rem !important;
    font-size: 1rem !important;
    font-weight: ${theme.weight.medium} !important;
    color: ${theme.color.text} !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .react-autosuggest__input::placeholder {
    color: ${theme.color.textSubtle} !important;
    font-weight: ${theme.weight.regular} !important;
    opacity: 1 !important;
  }

  /* The open dropdown is portal-rendered to document.body (see
     SuggestionsDropdown) so it escapes ancestor clipping — only the input
     lives inside the pill. */
`;

// Portal-rendered suggestions dropdown.  Mounted on document.body (not
// inside the pill) so it escapes every ancestor's overflow / transform /
// stacking context — HomepageAddressEntry is reused in 20+ sections across
// the site, each with different ancestors, so positioning the dropdown
// against the viewport is the only universally-correct fix.  The fixed
// top/left/width is set inline from the input's bounding rect.
export const SuggestionsDropdown = styled.div`
  border: 1px solid ${theme.color.border};
  background: ${theme.color.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.lg};
  overflow: hidden;

  .react-autosuggest__suggestions-list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
  }
  .react-autosuggest__suggestion {
    padding: 0.75rem 1.25rem;
    color: ${theme.color.text};
    font-size: 0.95rem;
    border-bottom: 1px solid ${theme.color.border};
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .react-autosuggest__suggestions-list li:last-child .react-autosuggest__suggestion {
    border-bottom: none;
  }
  .react-autosuggest__suggestion--highlighted {
    background-color: rgba(245, 168, 138, 0.14);
    color: ${theme.color.slate};
  }

  /* "Don't see your address? Search every state →" escape hatch, rendered
     as a footer below the DB matches.  Routes the typed query through the
     external geocoder so out-of-coverage addresses are still reachable. */
  .address-escape-hatch {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    border: none;
    border-top: 1px solid ${theme.color.border};
    background: rgba(245, 168, 138, 0.06);
    padding: 0.7rem 1.25rem;
    font-size: 0.88rem;
    color: ${theme.color.textMuted};
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .address-escape-hatch:hover {
    background: rgba(245, 168, 138, 0.14);
  }
  .address-escape-hatch i {
    color: ${theme.color.salmon};
    font-size: 0.82rem;
  }
  .address-escape-hatch strong {
    color: ${theme.color.salmonDeep};
    font-weight: ${theme.weight.semibold};
    white-space: nowrap;
  }
`;

// =============================================================
// /verify page chrome — custom layout that mirrors the
// PropertyDossierPage editorial pattern (cream PageWrap +
// hero band + a single white panel).  Avoids stacking
// PageLayout's FormCard around VerifyAddressForm's FormPanel,
// which is what made the page read as "tall and skinny" with
// triple-nested chrome.
// =============================================================

// Outer page background — matches the reward page after this
// step so /verify and /property-dossier feel like one
// continuous editorial document.
export const VerifyPageWrap = styled.div`
  background: var(--seo-cream);
  color: var(--color-text);
  min-height: 100vh;
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  font-feature-settings: "ss01", "cv11";
  padding-top: 4.5rem;
  padding-bottom: 3rem;

  @media (max-width: 768px) {
    padding-top: 4rem;
    padding-bottom: 2rem;
  }
`;

// Compact hero band — shorter than the reward page's hero
// because /verify still has a panel below it asking for
// action.  Coral hairline at top + gentle peach gradient
// keep the brand mark visible without dominating the page.
export const VerifyHeroBand = styled.section`
  position: relative;
  padding: 2.2rem 1.5rem 1.6rem;
  background:
    radial-gradient(circle at 12% -5%, rgba(227, 138, 102, 0.18) 0%, transparent 55%),
    radial-gradient(circle at 88% 110%, rgba(245, 168, 138, 0.12) 0%, transparent 55%),
    linear-gradient(180deg, var(--seo-cream) 0%, #fff5ef 100%);
  overflow: hidden;
  text-align: center;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(168, 90, 58, 0.4) 35%,
      rgba(227, 138, 102, 0.55) 65%,
      transparent 100%
    );
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: repeating-linear-gradient(
      135deg,
      rgba(16, 42, 67, 0.014) 0,
      rgba(16, 42, 67, 0.014) 1px,
      transparent 1px,
      transparent 8px
    );
    mix-blend-mode: multiply;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--seo-coral-text);
    margin-bottom: 0.75rem;
    position: relative;
    z-index: 1;
  }
  .hero-eyebrow::before,
  .hero-eyebrow::after {
    content: "";
    width: 28px;
    height: 1px;
    background: var(--seo-coral);
    opacity: 0.55;
  }

  .hero-title {
    font-size: clamp(1.55rem, 2.6vw + 0.6rem, 2.1rem);
    font-weight: 800;
    color: var(--seo-navy);
    line-height: 1.15;
    letter-spacing: -0.028em;
    margin: 0 0 0.55rem;
    max-width: 680px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
    text-wrap: balance;
  }

  .hero-lede {
    font-size: 1rem;
    color: var(--seo-slate);
    max-width: 560px;
    margin: 0 auto;
    line-height: 1.55;
    letter-spacing: -0.005em;
    position: relative;
    z-index: 1;
  }
  .hero-lede strong {
    color: var(--seo-navy);
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 1.7rem 1.2rem 1.3rem;
    .hero-lede { font-size: 0.94rem; }
  }
`;

// Wrapper around the form panel area — gives the panel a
// container for breathing room below the hero band.
export const VerifyMain = styled.div`
  padding: 2rem 1rem 0;

  @media (max-width: 768px) {
    padding: 1.4rem 0.85rem 0;
  }
`;

// =============================================================
// Heading slot rendered inside VerifyAddressForm's FormPanel —
// replaces the default FormHeading.  Carries the step pip,
// human-readable heading, and the inline "wrong address?"
// back link.  Lives inside the panel chrome (no nested card),
// which is the visual difference vs. the previous FormStep
// wrapper-around-FormPanel duplication.
// =============================================================
export const VerifyHeadingSlot = styled.div`
  margin: 0 0 1.2rem;

  .step-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--seo-coral-text);
    margin-bottom: 0.45rem;
  }
  .step-eyebrow .pip {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--seo-coral);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 800;
  }

  .step-heading {
    font-size: 1.18rem;
    font-weight: 700;
    color: var(--seo-navy);
    letter-spacing: -0.014em;
    margin: 0 0 0.4rem;
    line-height: 1.3;
  }

  .step-blurb {
    font-size: 0.92rem;
    color: var(--seo-slate);
    line-height: 1.55;
    margin: 0;
    max-width: 60ch;
    letter-spacing: -0.003em;
  }
  .step-back-link {
    background: none;
    border: none;
    padding: 0;
    margin-left: 0.4rem;
    font: inherit;
    color: var(--seo-coral-text);
    text-decoration: none;
    border-bottom: 1px solid rgba(168, 90, 58, 0.32);
    cursor: pointer;
    font-weight: 500;
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .step-back-link:hover {
    color: var(--seo-navy);
    border-bottom-color: var(--seo-navy);
  }
`;

// =============================================================
// Consolidated /verify form — additions injected via the
// VerifyAddressForm slots (belowFieldsAddon + belowMapAddon).
// Email + T&Cs live in the LEFT column under the property
// facts; reassurance lives in the RIGHT column under the map.
// Keeps the panel balanced horizontally instead of stacking
// every block full-width below the body.
// =============================================================

export const EmailGroup = styled.div`
  margin: 1.1rem 0 0;

  label {
    display: block;
    font-size: 0.92rem;
    font-weight: 600;
    color: var(--seo-navy);
    margin-bottom: 0.45rem;
    letter-spacing: -0.005em;
    line-height: 1.4;
  }

  input {
    width: 100%;
    background: var(--seo-paper);
    border: 1.5px solid ${theme.color.borderStrong};
    border-radius: ${theme.radius.md};
    padding: 0.7rem 0.9rem;
    font-size: 1rem;
    font-weight: ${theme.weight.medium};
    color: ${theme.color.text};
    box-shadow: ${theme.shadow.sm};
    outline: none;
    transition:
      border-color ${theme.motion.base},
      box-shadow ${theme.motion.base};
  }
  input:hover { border-color: ${theme.color.slateSoft}; }
  input:focus {
    border-color: ${theme.color.salmon};
    box-shadow: ${theme.shadow.sm}, ${theme.shadow.ring};
  }
  input[aria-invalid="true"] {
    border-color: ${theme.color.error};
    background: #fff6f5;
  }

  small {
    display: block;
    margin-top: 0.4rem;
    color: var(--seo-slate-mute);
    font-size: 0.82rem;
    line-height: 1.45;
  }
`;

// Optional primary-residence question.  Only renders when the
// appraised value crosses the free-report threshold; styled as
// a quiet inset to signal "this is a small clarification, not
// a hurdle."
export const PrimaryResidenceQuestion = styled.div`
  margin: 1.1rem 0 0;
  padding: 0.75rem 0.95rem;
  background: rgba(252, 234, 224, 0.55);
  border: 1px solid var(--seo-rule);
  border-left: 3px solid var(--seo-coral);
  border-radius: ${theme.radius.md};

  small {
    display: block;
    color: var(--seo-slate);
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 0.55rem;
  }
  small strong {
    color: var(--seo-navy);
  }

  .options {
    display: flex;
    gap: 1.5rem;
  }
  .options label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    cursor: pointer;
    font-size: 0.95rem;
    color: var(--seo-navy);
    font-weight: 500;
  }
  .options input[type="radio"] {
    accent-color: var(--seo-coral);
  }
`;

// Inline footnote row right under the property facts — pairs a
// "this is my primary residence" checkbox with the optional
// discount-code link.  Both sit at small body-copy weight so the
// row reads as a quiet attestation strip, not a competing form
// section.  Wraps to two lines on narrow viewports.
export const DiscountInline = styled.div`
  margin: 0.5rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  font-size: 0.85rem;
  color: var(--seo-slate-mute);
  min-height: 1.4rem;

  .primary-residence-inline {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    cursor: pointer;
    font-size: 0.85rem;
    color: ${theme.color.text};
    margin: 0;
    line-height: 1.3;
  }
  .primary-residence-inline input[type="checkbox"] {
    accent-color: var(--seo-coral);
    width: 0.95rem;
    height: 0.95rem;
    margin: 0;
  }

  .divider {
    color: var(--seo-rule);
    font-weight: 700;
  }

  /* Quiet, link-styled button — no border, no padding, just
     coral text with a hairline underline.  Visual weight
     matches an inline link, not a CTA. */
  .discount-quiet-link {
    background: none;
    border: none;
    padding: 0;
    color: var(--seo-coral-text);
    text-decoration: none;
    border-bottom: 1px solid rgba(168, 90, 58, 0.32);
    font: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    line-height: 1.3;
    transition: color ${theme.motion.quick}, border-color ${theme.motion.quick};
  }
  .discount-quiet-link:hover {
    color: var(--seo-navy);
    border-bottom-color: var(--seo-navy);
  }

  @media (max-width: 480px) {
    .divider { display: none; }
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const AppliedCodeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.22rem 0.65rem;
  background: var(--seo-mint);
  border: 1px solid var(--seo-mint-border);
  border-radius: ${theme.radius.pill};
  font-size: 0.82rem;
  color: var(--seo-mint-text);

  .dot {
    display: inline-flex;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 999px;
    background: var(--seo-mint-text);
    color: #fff;
    align-items: center;
    justify-content: center;
    font-size: 0.62rem;
    font-weight: 800;
  }

  strong {
    font-weight: 700;
    color: var(--seo-navy);
  }

  button {
    background: none;
    border: none;
    color: var(--seo-mint-text);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    cursor: pointer;
    font-size: 0.76rem;
    padding: 0 0 0 0.35rem;
    opacity: 0.75;
  }
  button:hover { opacity: 1; }
`;

// Certify block — two stacked check rows.  First confirms primary
// residence (always visible; matters for the under-$200k
// free-report path and is useful business signal at any tier).
// Second is the T&Cs acceptance.  Both deliberately understated
// so the form footer doesn't read as a wall of attestations.
export const CertifyAndSubmit = styled.div`
  margin: 1.4rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;

  .check-row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    cursor: pointer;
    font-size: 0.93rem;
    color: ${theme.color.text};
    line-height: 1.5;
    letter-spacing: -0.003em;
    margin: 0;
  }
  .check-row input[type="checkbox"] {
    margin-top: 0.18rem;
    flex: 0 0 auto;
    accent-color: var(--seo-coral);
    width: 1.05rem;
    height: 1.05rem;
  }
  .check-row .link {
    background: none;
    border: none;
    padding: 0;
    color: var(--seo-coral-text);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    font: inherit;
    cursor: pointer;
  }
  .check-row .link:hover { color: var(--seo-navy); }
`;

// Reassurance footer — sits in the right column under the map
// so the right rail balances the left column's height.  Quiet
// slate text + envelope glyph; cream paper background ties it
// to the surrounding panel chrome.
//
// Hidden below the desktop breakpoint because MapColumn flips
// to `order: -1` on mobile (the map is visual confirmation that
// renders above the fields), which would put this reassurance
// note before the email field — awkward.  On mobile the email
// field's own helper text covers the same ground.
export const ReassuranceNote = styled.div`
  margin: 0;
  padding: 0.75rem 0.9rem;
  background: rgba(253, 251, 247, 0.85);
  border: 1px dashed var(--seo-rule);
  border-radius: ${theme.radius.md};
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.84rem;
  color: var(--seo-slate);
  line-height: 1.5;

  span[aria-hidden="true"] {
    font-size: 1.05rem;
    line-height: 1;
    margin-top: 0.05rem;
  }

  @media (max-width: 991px) {
    display: none;
  }
`;
